import React, { useState, useEffect } from 'react';
import { Download, FileText, CalendarDays } from 'lucide-react';
import { useMyContext } from '../../../context/MyContext';
import NavBar from '../../../component/b2b/navBar/b2bNavBar';
import Sidebar from '../../../component/b2b/sideBar/b2bSideBar';
import { Base_Url, b2bAllShipmentListAll } from '../../../config/config';
import { TbReportSearch } from "react-icons/tb";
import '../../../css/custom-scrollbar.css'
import { toast } from 'react-toastify';
import Papa from "papaparse";
import FullPageLoader from '../../../../src/component/fullPageLoader/fullPageLoader'

const B2bReports = () => {
  const { isDarkMode, isToggleSidebar } = useMyContext();
  const [isPageLoading, isSetPageLoading] = useState();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dateRanges, setDateRanges] = useState({
    'MIS Report': { startDate: '', endDate: '' },
    'Order Product Report': { startDate: '', endDate: '' },
    'Current Shipment Report': { startDate: '', endDate: '' },
    'NDR Report': { startDate: '', endDate: '' }
  });

  const validateDate = (date) => {
    const selectedDate = new Date(date);
    const today = new Date();
    return selectedDate <= today;
  };

  // Helper to safely parse JSON strings from API
  const safeParseJSON = (str) => {
    try {
      return typeof str === 'string' ? JSON.parse(str) : str;
    } catch {
      return {};
    }
  };

  // Flatten a raw API item into a usable object with dot-notation keys
  const flattenItem = (item) => {
    const pickup = safeParseJSON(item.pickup_location || '{}');
    const dropoff = safeParseJSON(item.dropoff_location || '{}');
    const billing = safeParseJSON(item.billing_address || '{}');
    const shipmentDetails = safeParseJSON(item.shipment_details || '[]');
    const invoices = safeParseJSON(item.invoices || '[]');
    const firstShipment = Array.isArray(shipmentDetails) && shipmentDetails.length > 0 ? shipmentDetails[0] : {};
    const firstInvoice = Array.isArray(invoices) && invoices.length > 0 ? invoices[0] : {};

    return {
      // Top-level fields
      order_id: item.order_id,
      user_id: item.user_id || item.retailer_id,
      retailer_id: item.retailer_id,
      status: item.status,
      payment_mode: item.payment_mode,
      payment_id: item.payment_id,
      cod_amount: item.cod_amount,
      total_weight: item.total_weight,
      total_boxes: item.total_boxes,
      total_length: item.total_length,
      total_width: item.total_width,
      total_height: item.total_height,
      shipment_type: item.shipment_type,
      courier_partner: item.courier_partner,
      debit_amount: item.debit_amount,
      request_id: item.request_id,
      waybill: item.waybill,
      freight_mode: item.freight_mode,
      created_at: item.created_at,
      updated_at: item.updated_at,
      remarks: item.remarks,

      // Pickup location (pickup_location JSON)
      pickup_name: pickup.consignee_name,
      pickup_address: pickup.address,
      pickup_city: pickup.city,
      pickup_state: pickup.state,
      pickup_pincode: pickup.zip,
      pickup_phone: pickup.phone,

      // Drop location (dropoff_location JSON)
      drop_name: dropoff.consignee_name,
      drop_address: dropoff.address,
      drop_city: dropoff.city,
      drop_state: dropoff.state,
      drop_pin: dropoff.zip,
      drop_phone: dropoff.phone,
      email: dropoff.email,

      // Billing address
      billing_name: billing.name,
      billing_company: billing.company,
      billing_address: billing.address,
      billing_city: billing.city,
      billing_state: billing.state,
      billing_pin: billing.pin,
      billing_phone: billing.phone,
      pan_number: billing.pan_number,
      gst_number: billing.gst_number,

      // Shipment details (first entry)
      product_desc: firstShipment.description,
      quantity: firstShipment.box_count,
      weight: firstShipment.weight,

      // Invoice details (first entry)
      invoice_number: firstInvoice.inv_num,
      invoice_amount: firstInvoice.inv_amt,
      ewaybill: firstInvoice.ewaybill,

      // NDR related (may not exist, default N/A)
      is_ndr: item.is_ndr,
      ndr_attempt: item.ndr_attempt,
      ndr_reason: item.ndr_reason,
    };
  };

  const reports = [
    {
      name: 'MIS Report',
      description: 'Management information system report with detailed order',
      columnMapping: {
        'Order ID': 'order_id',
        'User ID': 'user_id',
        'MWB': 'master_waybill',
        'Lrn_no': 'lrn',
        'Pickup Name': 'pickup_name',
        'Pickup Address': 'pickup_address',
        'Pickup City': 'pickup_city',
        'Pickup Pin Code': 'pickup_pincode',
        'Pickup State': 'pickup_state',
        'Pickup Phone': 'pickup_phone',
        'Consignee Name': 'drop_name',
        'Destination Address': 'drop_address',
        'Destination Pincode': 'drop_pin',
        'Destination City': 'drop_city',
        'Destination State': 'drop_state',
        'Destination Phone': 'drop_phone',
        'Email': 'email',
        'Payment Mode': 'payment_mode',
        'COD Amount': 'cod_amount',
        'Total Amount': 'invoice_amount',
        'Invoice Number': 'invoice_number',
        'E-Waybill': 'ewaybill',
        'Product Description': 'product_desc',
        'Quantity (Boxes)': 'quantity',
        'Weight (kg)': 'total_weight',
        'Length': 'total_length',
        'Width': 'total_width',
        'Height': 'total_height',
        'Freight Mode': 'freight_mode',
        'Courier Partner': 'courier_partner',
        'Shipment Type': 'shipment_type',
        'Request ID': 'request_id',
        'Status': 'status',
        'Debit Amount': 'debit_amount',
        'GST Number': 'gst_number',
        'PAN Number': 'pan_number',
        'Remarks': 'remarks',
        'Created At': 'created_at',
        'Updated At': 'updated_at',
      }
    },
    {
      name: 'Order Product Report',
      description: 'Detailed product information for all orders',
      columnMapping: {
        'Order ID': 'order_id',
        'User ID': 'user_id',
        'Drop Name': 'drop_name',
        'Email': 'email',
        'Product Description': 'product_desc',
        'Quantity (Boxes)': 'quantity',
        'Weight (Kg)': 'total_weight',
        'Invoice Number': 'invoice_number',
        'Invoice Amount': 'invoice_amount',
        'E-Waybill': 'ewaybill',
        'AWB Number': 'waybill',
        'Freight Mode': 'freight_mode',
        'Created At': 'created_at',
      }
    },
    {
      name: 'Current Shipment Report',
      description: 'Real-time status of all active shipments',
      columnMapping: {
        'Order ID': 'order_id',
        'User ID': 'user_id',
        'Drop Name': 'drop_name',
        'Product Description': 'product_desc',
        'Quantity (Boxes)': 'quantity',
        'Weight (Kg)': 'total_weight',
        'AWB Number': 'waybill',
        'Drop Address': 'drop_address',
        'Drop City': 'drop_city',
        'Drop State': 'drop_state',
        'Drop Pin': 'drop_pin',
        'Drop Phone': 'drop_phone',
        'Courier Partner': 'courier_partner',
        'Status': 'status',
        'Updated At': 'updated_at',
      }
    },
    {
      name: 'NDR Report',
      description: 'Non-delivery report with attempt details and reasons',
      columnMapping: {
        'Order ID': 'order_id',
        'User ID': 'user_id',
        'Drop Name': 'drop_name',
        'Drop Phone': 'drop_phone',
        'Product Description': 'product_desc',
        'AWB Number': 'waybill',
        'Drop Address': 'drop_address',
        'Drop Pin': 'drop_pin',
        'Drop City': 'drop_city',
        'Drop State': 'drop_state',
        'Status': 'status',
        'NDR Attempt': 'ndr_attempt',
        'NDR Reason': 'ndr_reason',
        'Updated At': 'updated_at',
      }
    }
  ];

  useEffect(() => {
    fetchOrderData();
  }, []);

  const fetchOrderData = async () => {
    setLoading(true);
    setError(null);
    isSetPageLoading(true);
    const token = localStorage.getItem('token');
    const cusid = localStorage.getItem('cus_id');
    try {
      const response = await fetch(Base_Url + b2bAllShipmentListAll, {
        method: 'GET',
        headers: {
          token,
          cus_id: cusid,
          'Content-Type': 'application/json',
        },
      });
      const result = await response.json();
      if (response.ok) {
        // Flatten all items at fetch time so reports share the same processed data
        const flattened = (result.data || []).map(flattenItem);
        setData(flattened);
        isSetPageLoading(false);
      } else {
        setError(result.message);
        isSetPageLoading(false);
      }
    } catch (error) {
      setError('Failed to fetch data. Please try again later.');
      isSetPageLoading(false);
    } finally {
      setLoading(false);
      isSetPageLoading(false);
    }
  };

  const handleDateChange = (reportName, field, value) => {
    if (value && !validateDate(value)) {
      toast.error('Future dates are not allowed');
      return;
    }
    if (field === 'endDate' && value) {
      const startDate = dateRanges[reportName].startDate;
      if (startDate && new Date(value) < new Date(startDate)) {
        toast.error('End date cannot be before start date');
        return;
      }
    }
    if (field === 'startDate' && value) {
      const endDate = dateRanges[reportName].endDate;
      if (endDate && new Date(value) > new Date(endDate)) {
        toast.error('Start date cannot be after end date');
        return;
      }
    }
    setDateRanges(prev => ({
      ...prev,
      [reportName]: {
        ...prev[reportName],
        [field]: value
      }
    }));
  };

  const isDownloadDisabled = (reportName) => {
    const { startDate, endDate } = dateRanges[reportName];
    return loading || !startDate || !endDate;
  };

  const downloadCSV = async (report) => {
    const { startDate, endDate } = dateRanges[report.name];

   
  let filteredData = data;
  if (startDate && endDate) {
    filteredData = data.filter(item => {
      // Extract just the date part "YYYY-MM-DD" from "2026-03-27 21:58:24"
      const itemDateStr = item.created_at?.split(' ')[0];
      return itemDateStr >= startDate && itemDateStr <= endDate;
    });
  }

    if (filteredData.length === 0) {
      toast.error('No data found for the selected date range');
      return;
    }

    const csvData = filteredData.map(item => {
      const row = {};
      Object.entries(report.columnMapping).forEach(([displayName, apiKey]) => {
        row[displayName] = item[apiKey] !== undefined && item[apiKey] !== null && item[apiKey] !== ''
          ? item[apiKey]
          : 'N/A';
      });
      return row;
    });

    try {
      const csv = Papa.unparse(csvData, {
        header: true,
        quotes: true,
        quoteChar: '"',
        escapeChar: '"'
      });
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${report.name}_${startDate || 'all'}_to_${endDate || 'all'}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`${report.name} downloaded successfully!`);
    } catch (error) {
      console.error('Error generating CSV:', error);
      toast.error('Failed to generate CSV file');
    }
  };

  return (
    <div className={`h-screen flex flex-col overflow-hidden ${isDarkMode ? 'dark:bg-darkmodeColor' : 'bg-white'}`}>
      <div className="z-10">
        <NavBar />
      </div>
      <div className="flex flex-1 overflow-hidden">
        {isToggleSidebar && (
          <div
            className={`fixed z-20 h-full w-64 md:relative duration-300 bg-gray-200 dark:bg-darkmodeColor dark:text-white md:bg-transparent mt-4 md:w-64 md:z-20`}
            style={
              window.innerWidth < 768
                ? { top: "113px", height: "calc(100vh - 115px)", overflowY: 'auto' }
                : {}
            }>
            <Sidebar />
          </div>
        )}
        <FullPageLoader isLoading={isPageLoading} message="Processing your request..." />

        {/* Main content — fills remaining height, only this area scrolls if needed */}
        <div className={`flex-1 flex flex-col overflow-hidden bg-gray-200 ${isDarkMode ? 'dark:bg-darkmodeColor text-white' : ''}`}>
          <div className="flex-1 overflow-y-auto custom-scrollbar px-2 md:px-2 py-2">

            {/* Header */}
            <div className="mb-5 flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-black flex items-center justify-center flex-shrink-0">
                <TbReportSearch className="text-white" size={20} />
              </div>
              <div>
                <h1 className="text-base sm:text-lg font-bold text-gray-900 dark:text-white leading-tight">Report Dashboard</h1>
                <p className="text-[11px] text-gray-400">Select a date range and export the report you need</p>
              </div>
            </div>

            {error && (
              <div className="mb-2 p-3 bg-white border border-black/10 rounded-lg flex items-start gap-2">
                <span className="text-black font-semibold text-xs mt-0.5">Error</span>
                <p className="text-gray-700 text-xs">{error}</p>
              </div>
            )}

            {/* Report cards */}
            <div className="grid gap-4 md:grid-cols-2">
              {reports.map((report) => (
                <div
                  key={report.name}
                  className="bg-white dark:bg-darkmodeColor rounded-2xl border border-gray-200 dark:border-gray-700 hover:border-black dark:hover:border-white hover:shadow-md transition-all duration-200 p-2 flex flex-col"
                >
                  <div className="mb-4 flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg bg-gray-100 dark:bg-gray-800 flex items-center justify-center flex-shrink-0">
                      <FileText size={16} className="text-black dark:text-white" />
                    </div>
                    <div className="min-w-0">
                      <h2 className="text-sm font-bold text-gray-900 dark:text-white leading-tight">
                        {report.name}
                      </h2>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {report.description}
                      </p>
                      <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-1 font-medium">
                        {Object.keys(report.columnMapping).length} fields included
                      </p>
                    </div>
                  </div>

                  <div className="space-y-3 mt-auto">
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <label className="flex items-center gap-1 text-[11px] font-semibold text-gray-600 dark:text-gray-300">
                          <CalendarDays size={11} /> Start Date
                        </label>
                        <input
                          type="date"
                          value={dateRanges[report.name].startDate}
                          onChange={(e) => handleDateChange(report.name, 'startDate', e.target.value)}
                          className="w-full px-2.5 py-1.5 text-xs border border-gray-200 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-black/20 focus:border-black dark:bg-gray-800 dark:border-gray-600 dark:text-white"
                        />
                      </div>
                      <div className="space-y-1">
                        <label className="flex items-center gap-1 text-[11px] font-semibold text-gray-600 dark:text-gray-300">
                          <CalendarDays size={11} /> End Date
                        </label>
                        <input
                          type="date"
                          value={dateRanges[report.name].endDate}
                          onChange={(e) => handleDateChange(report.name, 'endDate', e.target.value)}
                          className="w-full px-2.5 py-1.5 text-xs border border-gray-200 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-black/20 focus:border-black dark:bg-gray-800 dark:border-gray-600 dark:text-white"
                        />
                      </div>
                    </div>
                    <button
                      onClick={() => downloadCSV(report)}
                      disabled={isDownloadDisabled(report.name)}
                      className="w-full flex items-center justify-center gap-2 px-4 py-2 text-xs font-semibold rounded-lg text-white bg-black hover:bg-gray-800 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    >
                      <Download size={14} />
                      Download CSV
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default B2bReports;